import { MatDialogConfig } from '@angular/material/dialog';
import {
  AvailabilityColumnKey,
  InventoryColumnKey,
  InventoryTabKeys,
} from '../enums/inventory-table-columns.enum';
import { FILTER_RANGE_DEFAULTS } from '../helpers/inventory-filter.helpers';
import { InventoryItemFullInformation } from '../interfaces/inventory-item.interface';

export interface TableColumn {
  key: InventoryColumnKey;
  label: string;
  sortable?: boolean;
}

export interface EditInventoryTab {
  key: InventoryTabKeys;
  label: string;
}

export const INVENTORY_TABLE_COLUMNS: TableColumn[] = [
  {
    key: InventoryColumnKey.PRODUCT_UPC_AND_NAME,
    label: 'inventory.tableHeaders.productUpcAndName',
    sortable: true,
  },
  { key: InventoryColumnKey.BRAND_NAME, label: 'inventory.tableHeaders.brand', sortable: true },
  {
    key: InventoryColumnKey.PRODUCT_LOCATION,
    label: 'inventory.tableHeaders.location',
    sortable: true,
  },
  {
    key: InventoryColumnKey.PRODUCT_CATEGORY,
    label: 'inventory.tableHeaders.category',
    sortable: true,
  },
  {
    key: InventoryColumnKey.AVAILABLE_AMOUNT,
    label: 'inventory.tableHeaders.available',
    sortable: true,
  },
  {
    key: InventoryColumnKey.RESERVED_AMOUNT,
    label: 'inventory.tableHeaders.reserved',
    sortable: true,
  },
  {
    key: InventoryColumnKey.ON_HAND_AMOUNT,
    label: 'inventory.tableHeaders.onHand',
    sortable: true,
  },
  { key: InventoryColumnKey.PRICE, label: 'inventory.tableHeaders.price', sortable: true },
  {
    key: InventoryColumnKey.LAST_UPDATED_DATE,
    label: 'inventory.tableHeaders.date',
    sortable: true,
  },
  { key: InventoryColumnKey.STATUS, label: 'inventory.tableHeaders.status', sortable: true },
  { key: InventoryColumnKey.ACTIONS, label: 'inventory.tableHeaders.actions', sortable: false },
];

export const PRODUCT_FIELD_KEYS = {
  productType: 'productType',
  productName: 'productName',
  productBrand: 'productBrand',
  productUpc: 'productUpc',
  productCategory: 'productCategory',
  priceUsd: 'priceUsd',
  productDescription: 'productDescription',
} as const;

export type AllProductFieldKey = keyof typeof PRODUCT_FIELD_KEYS;

export type ProductFieldKey = Exclude<AllProductFieldKey, 'productType'>;
export type InvalidKeys = Exclude<ProductFieldKey, 'productCategory'>;

export const PRODUCT_GENERAL_FIELDS = [
  {
    key: PRODUCT_FIELD_KEYS.productName,
    label: 'inventory.productFields.productName',
    value: (item: InventoryItemFullInformation) => item.productName,
    placeholder: 'inventory.placeholders.productName',
  },
  {
    key: PRODUCT_FIELD_KEYS.productBrand,
    label: 'inventory.productFields.productBrand',
    value: (item: InventoryItemFullInformation) => item.brand?.name,
    placeholder: 'inventory.placeholders.productBrand',
  },
  {
    key: PRODUCT_FIELD_KEYS.productUpc,
    label: 'inventory.productFields.productUpc',
    value: (item: InventoryItemFullInformation) => item.productUpc,
    placeholder: 'inventory.placeholders.productUpc',
  },
  {
    key: PRODUCT_FIELD_KEYS.productType,
    label: 'inventory.productFields.productType',
    value: (item: InventoryItemFullInformation) => item.hasVariations,
    placeholder: 'inventory.placeholders.productType',
  },
  {
    key: PRODUCT_FIELD_KEYS.productCategory,
    label: 'inventory.productFields.productCategory',
    value: (item: InventoryItemFullInformation) => item.mainCategory?.name,
    placeholder: 'inventory.placeholders.category',
  },
  {
    key: PRODUCT_FIELD_KEYS.priceUsd,
    label: 'inventory.productFields.priceUsd',
    value: (item: InventoryItemFullInformation) => item.price,
    placeholder: 'inventory.placeholders.price',
    pipe: { name: 'number', args: '1.2-2' },
  },
  {
    key: PRODUCT_FIELD_KEYS.productDescription,
    label: 'inventory.productFields.productDescription',
    value: (item: InventoryItemFullInformation) => item.description,
    placeholder: 'inventory.placeholders.productDescription',
    class: 'general-info__field--description',
  },
];

export const EMPTY_INVENTORY_ITEM: InventoryItemFullInformation = {
  id: '',
  productUpc: 0,
  productName: '',
  description: '',
  availableAmount: 0,
  reservedAmount: 0,
  onHandAmount: 0,
  price: 0,
  isPromoted: false,
  status: 'Available',
  productCategories: [],
  lastUpdatedDate: new Date(),
  productLocation: '',
  brand: {
    id: 0,
    name: '',
  },
  hasVariations: false,
  images: [],
  productDetails: [],
  mainCategory: {
    id: 0,
    name: '',
  },
};

export const REQUIRED_ERROR_MESSAGES: Record<ProductFieldKey, string> = {
  productName: 'inventory.fieldsErrors.productName.required',
  productBrand: 'inventory.fieldsErrors.productBrand.required',
  productUpc: 'inventory.fieldsErrors.productUpc.required',
  productCategory: 'inventory.fieldsErrors.productCategory.required',
  priceUsd: 'inventory.fieldsErrors.priceUsd.required',
  productDescription: 'inventory.fieldsErrors.productDescription.required',
};

export const INVALID_ERROR_MESSAGES: Record<InvalidKeys, string> = {
  productName: 'inventory.fieldsErrors.productName.pattern',
  productBrand: 'inventory.fieldsErrors.productBrand.pattern',
  productUpc: 'inventory.fieldsErrors.productUpc.pattern',
  priceUsd: 'inventory.fieldsErrors.priceUsd.pattern',
  productDescription: 'inventory.fieldsErrors.productDescription.pattern',
};

export const EDIT_INVENTORY_TABS: EditInventoryTab[] = [
  {
    key: InventoryTabKeys.general,
    label: 'inventory.tabs.general',
  },
  {
    key: InventoryTabKeys.price,
    label: 'inventory.tabs.price',
  },
  {
    key: InventoryTabKeys.availability,
    label: 'inventory.tabs.availability',
  },
  {
    key: InventoryTabKeys.media,
    label: 'inventory.tabs.media',
  },
  {
    key: InventoryTabKeys.productDetails,
    label: 'inventory.tabs.productDetails',
  },
  {
    key: InventoryTabKeys.variants,
    label: 'inventory.tabs.variants',
  },
];

export const COLLAPSED_HEADER_SCROLL_THRESHOLD = 70;
export const ACTIVE_SECTION_THRESHOLD = 150;

export type PromotionType = 'fixed' | 'percentage';

interface PromotionTypeOption {
  key: PromotionType;
  value: PromotionType;
  label: string;
}

export const PROMOTION_TYPES: PromotionTypeOption[] = [
  {
    key: 'fixed',
    value: 'fixed',
    label: '$',
  },
  {
    key: 'percentage',
    value: 'percentage',
    label: '%',
  },
];

interface AvailabilityTableColumn {
  key: AvailabilityColumnKey;
  label: string;
  editable?: boolean;
  readonly?: boolean;
}

export const AVAILABILITY_COLUMNS: AvailabilityTableColumn[] = [
  {
    key: AvailabilityColumnKey.LOCATION,
    label: 'inventory.availability.location',
    editable: false,
    readonly: false,
  },
  {
    key: AvailabilityColumnKey.ON_HAND,
    label: 'inventory.availability.onHand',
    editable: true,
  },
  {
    key: AvailabilityColumnKey.RESERVED,
    label: 'inventory.availability.reserved',
    editable: true,
  },
  {
    key: AvailabilityColumnKey.INCOMING,
    label: 'inventory.availability.incoming',
    editable: true,
  },
  {
    key: AvailabilityColumnKey.AVAILABLE,
    label: 'inventory.availability.available',
    readonly: true,
  },
];

export const INITIAL_FILTER_VALUES = {
  category: '',
  status: '',
  sortBy: '',
  sortOrder: '',
  priceMin: FILTER_RANGE_DEFAULTS.MIN,
  priceMax: FILTER_RANGE_DEFAULTS.MAX,
  availableAmountMin: FILTER_RANGE_DEFAULTS.MIN,
  availableAmountMax: FILTER_RANGE_DEFAULTS.MAX,
  reservedAmountMin: FILTER_RANGE_DEFAULTS.MIN,
  reservedAmountMax: FILTER_RANGE_DEFAULTS.MAX,
  onHandAmountMin: FILTER_RANGE_DEFAULTS.MIN,
  onHandAmountMax: FILTER_RANGE_DEFAULTS.MAX,
} as const;

export const INVENTORY_SORT_OPTIONS = INVENTORY_TABLE_COLUMNS.filter(
  (column) => column.sortable,
).map(({ key, label }) => ({
  value: key,
  label,
}));

export const INVENTORY_SORT_DIRECTIONS = [
  { value: 'asc', label: 'inventory.filter.sortAscending' },
  { value: 'desc', label: 'inventory.filter.sortDescending' },
] as const;

export const UPLOAD_INVENTORY_DIALOG_CONFIG: MatDialogConfig = {
  width: '600px',
  maxWidth: '90vw',
  disableClose: false,
  autoFocus: false,
  panelClass: 'no-radius-dialog',
};
